// frontend/src/pages/admin/LearningDevelopment/components/CourseLibraryTab.js
import React, { useState, useMemo } from 'react';
import {
  BookOpen, Building, Plus, Globe, Download, X, ChevronsUpDown, ChevronDown,
  Layers3, Pencil,
} from 'lucide-react';
import { CountBadge } from '../../../../components/UI/CountBadge';
import { StatusBadge } from '../../../../components/UI/StatusBadge';
import LoadingSpinner from '../../../../components/UI/LoadingSpinner';
import AccordionSection from '../../../../components/UI/AccordionSection';
import { FilterButton } from '../../../../components/UI/FilterButton';
import DebouncedSearchInput from '../../../../components/UI/DebouncedSearchInput';
import Modal from '../../../../components/UI/Modal';
import FormSelect from '../../../../components/UI/FormSelect';
import SearchableCombobox from '../../../../components/UI/SearchableCombobox';
import toast from '../../../../utils/toast';
import {
  useCourseLibrary,
  useCreateMasterCourse,
  useUpdateMasterCourse,
} from '../../../../hooks/useCourseRequests';
import { useSafetyCertificationTypes } from '../../../../hooks/useMandatoryCertCompliance';

const COURSE_TYPE_OPTIONS = [
  { value: 'initial', label: 'Initial Certification' },
  { value: 'renewal', label: 'Renewal / Recertification' },
  { value: 'refresher', label: 'Refresher (no cert)' },
  { value: 'awareness', label: 'Awareness (no cert)' },
];

const REGULATORY_BODY_OPTIONS = [
  { value: '', label: 'Not specified' },
  { value: 'MOM', label: 'MOM — Ministry of Manpower' },
  { value: 'BCA', label: 'BCA — Building and Construction Authority' },
  { value: 'SSG', label: 'SSG — SkillsFuture Singapore' },
  { value: 'SCDF', label: 'SCDF — Singapore Civil Defence Force' },
  { value: 'WSH', label: 'WSH — Workplace Safety and Health Council' },
  { value: 'OTHER', label: 'Other' },
];

const REGULATORY_BODY_FILTER_OPTIONS = [
  { value: 'all', label: 'All Regulatory Bodies' },
  { value: 'MOM', label: 'MOM — Ministry of Manpower' },
  { value: 'BCA', label: 'BCA — Building and Construction Authority' },
  { value: 'SSG', label: 'SSG — SkillsFuture Singapore' },
  { value: 'SCDF', label: 'SCDF — Singapore Civil Defence Force' },
  { value: 'WSH', label: 'WSH — Workplace Safety and Health Council' },
  { value: 'OTHER', label: 'Other' },
];

const getRegulatoryBodyLabel = (value) => {
  const match = REGULATORY_BODY_FILTER_OPTIONS.find((opt) => opt.value === value);
  return match ? match.label.split(' — ')[0] : value;
};

const emptyMasterCourseForm = () => ({
  code: '',
  name: '',
  description: '',
  course_type: 'initial',
  regulatory_body: '',
  grants_certification: '',
  typical_duration_hours: '',
  typical_duration_days: '',
  is_active: true,
  notes: '',
});

const MasterCourseFormModal = ({ course, onClose }) => {
  const isEdit = Boolean(course);
  const { data: certTypes = [] } = useSafetyCertificationTypes();
  const createMutation = useCreateMasterCourse();
  const updateMutation = useUpdateMasterCourse();

  const [form, setForm] = useState(() =>
    course
      ? {
          code: course.code,
          name: course.name || '',
          description: course.description || '',
          course_type: course.course_type || 'initial',
          regulatory_body: course.regulatory_body || '',
          grants_certification: course.grants_certification || '',
          typical_duration_hours: course.typical_duration_hours ?? '',
          typical_duration_days: course.typical_duration_days ?? '',
          is_active: course.is_active ?? true,
          notes: course.notes || '',
        }
      : emptyMasterCourseForm());

  const selectedCert = useMemo(
    () => certTypes.find((ct) => String(ct.id) === String(form.grants_certification)) || null,
    [certTypes, form.grants_certification],
  );

  const saving = createMutation.isPending || updateMutation.isPending;

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!form.code.trim()) { toast.error('Course code is required'); return; }
    if (!form.name.trim()) { toast.error('Course name is required'); return; }

    const payload = {
      code: form.code.trim().toUpperCase(),
      name: form.name.trim(),
      description: form.description.trim(),
      course_type: form.course_type,
      regulatory_body: form.regulatory_body,
      grants_certification: form.grants_certification || null,
      typical_duration_hours: form.typical_duration_hours === '' ? null : Number(form.typical_duration_hours),
      typical_duration_days: form.typical_duration_days === '' ? null : Number(form.typical_duration_days),
      is_active: form.is_active,
      notes: form.notes.trim(),
    };

    const onSuccess = () => onClose();
    if (isEdit) {
      updateMutation.mutate({ code: course.code, data: payload }, { onSuccess });
    } else {
      createMutation.mutate(payload, { onSuccess });
    }
  };

  return (
    <Modal
      isOpen
      onClose={onClose}
      title={isEdit ? 'Edit Master Course' : 'Add Master Course'}
      size="lg"
      theme="admin"
      footer={(
        <>
          <button type="button" onClick={onClose} className="btn btn-admin-outline">
            Cancel
          </button>
          <button
            type="submit"
            form="master-course-form"
            disabled={saving}
            className="btn btn-admin-primary"
          >
            {saving ? 'Saving…' : isEdit ? 'Save changes' : 'Add course'}
          </button>
        </>
      )}
    >
      <form id="master-course-form" onSubmit={handleSubmit} className="space-y-5">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Course code <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              className="input-admin w-full uppercase"
              placeholder="e.g. CET-EW"
              value={form.code}
              disabled={isEdit}
              onChange={(e) => setForm((p) => ({ ...p, code: e.target.value.toUpperCase() }))}
            />
            {isEdit && (
              <p className="text-xs text-gray-400 mt-1">Code cannot be changed after creation.</p>
            )}
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Course type <span className="text-red-500">*</span>
            </label>
            <FormSelect
              options={COURSE_TYPE_OPTIONS}
              value={form.course_type}
              onChange={(value) => setForm((p) => ({ ...p, course_type: value }))}
              theme="admin"
              required
            />
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Course name <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            className="input-admin w-full"
            placeholder="e.g. CET Electrical Wiring"
            value={form.name}
            onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
          <textarea
            rows={2}
            className="textarea-admin w-full"
            value={form.description}
            onChange={(e) => setForm((p) => ({ ...p, description: e.target.value }))}
          />
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Regulatory body</label>
            <FormSelect
              options={REGULATORY_BODY_OPTIONS}
              value={form.regulatory_body}
              onChange={(value) => setForm((p) => ({ ...p, regulatory_body: value }))}
              theme="admin"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Grants certification <span className="font-normal text-gray-400">optional</span>
            </label>
            <SearchableCombobox
              items={certTypes}
              value={form.grants_certification}
              onChange={(value) => setForm((p) => ({ ...p, grants_certification: value || '' }))}
              placeholder="No certification"
              searchPlaceholder="Search certifications…"
              emptyLabel="No matching certifications"
              clearLabel="No certification"
              variant="form"
              theme="admin"
              displayFn={(ct) => ct.name}
              valueFn={(ct) => ct.id}
              displayValue={selectedCert ? selectedCert.name : ''}
              searchFn={(ct, term) => (ct.name || '').toLowerCase().includes(term)}
            />
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Typical duration (days)</label>
            <input
              type="number"
              min="0"
              step="0.5"
              className="input-admin w-full"
              value={form.typical_duration_days}
              onChange={(e) => setForm((p) => ({ ...p, typical_duration_days: e.target.value }))}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Typical duration (hours)</label>
            <input
              type="number"
              min="0"
              className="input-admin w-full"
              value={form.typical_duration_hours}
              onChange={(e) => setForm((p) => ({ ...p, typical_duration_hours: e.target.value }))}
            />
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Internal notes</label>
          <textarea
            rows={2}
            className="textarea-admin w-full"
            value={form.notes}
            onChange={(e) => setForm((p) => ({ ...p, notes: e.target.value }))}
          />
        </div>

        <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
          <input
            type="checkbox"
            className="rounded border-gray-300 accent-red-600 text-red-600 focus:ring-red-500"
            checked={form.is_active}
            onChange={(e) => setForm((p) => ({ ...p, is_active: e.target.checked }))}
          />
          Course is active (visible in the library and course request pickers)
        </label>
      </form>
    </Modal>
  );
};

const LANG_LABELS = {
  en: 'EN', zh: 'ZH', ta: 'TA', bn: 'BN', my: 'MY', ms: 'MS',
};

const normalizeLang = (code) => {
  if (!code) return null;
  const key = String(code).toLowerCase();
  return LANG_LABELS[key] || String(code).toUpperCase();
};

const getActiveProviderMappings = (course) =>
  (course.provider_mappings || []).filter((mapping) => mapping.is_active !== false);

/**
 * Group provider_mappings rows by training centre.
 * One centre may have multiple language/catalogue variants.
 */
const groupMappingsByProvider = (mappings = []) => {
  const groups = new Map();

  mappings.forEach((mapping) => {
    const key =
      mapping.training_provider_uen ||
      mapping.training_provider ||
      mapping.training_provider_name ||
      'unknown';

    if (!groups.has(key)) {
      const trading = mapping.training_provider_trading_name?.trim() || '';
      const legal = mapping.training_provider_name?.trim() || '';
      const display =
        mapping.provider_display_name?.trim() ||
        trading ||
        legal ||
        'Unknown Provider';

      groups.set(key, {
        key,
        displayName: display,
        tradingName: trading,
        legalName: legal,
        languages: new Set(),
        titles: [],
      });
    }

    const group = groups.get(key);
    (mapping.languages_available || []).forEach((lang) => {
      const label = normalizeLang(lang);
      if (label) group.languages.add(label);
    });

    if (mapping.provider_course_name) {
      group.titles.push({
        id: mapping.id,
        name: mapping.provider_course_name,
        languages: (mapping.languages_available || []).map(normalizeLang).filter(Boolean),
      });
    }
  });

  return Array.from(groups.values())
    .map((g) => ({
      ...g,
      languages: Array.from(g.languages).sort(),
      titleCount: g.titles.length,
    }))
    .sort((a, b) => a.displayName.localeCompare(b.displayName));
};

/** Compact cert label for accordion subtitle — short_name preferred. */
const getCertificationLabel = (course) => {
  const shortName = course.grants_certification_short_name?.trim();
  if (shortName) return shortName;

  const fullName = course.grants_certification_name?.trim();
  if (!fullName) return null;

  // Prefer parenthetical abbrev if present, e.g. "... (CSOC)"
  const paren = fullName.match(/\(([A-Z0-9-]{2,12})\)\s*$/);
  if (paren) return paren[1];

  return fullName;
};

/** Hide cert line when course title/code already signals the same competency. */
const shouldShowCertificationLine = (course, certLabel) => {
  if (!certLabel) return false;
  const haystack = `${course.code || ''} ${course.name || ''}`.toLowerCase();
  return !haystack.includes(certLabel.toLowerCase());
};

const MetricCard = ({ icon: Icon, iconClasses, value, label }) => (
  <div className="bg-white rounded-2xl shadow-sm border p-4 sm:p-6">
    <div className="flex items-center">
      <div className={`w-9 h-9 sm:w-12 sm:h-12 rounded-full flex items-center justify-center flex-shrink-0 ${iconClasses}`}>
        <Icon className="w-5 h-5 sm:w-6 sm:h-6" />
      </div>
      <div className="ml-2 sm:ml-4 min-w-0">
        <p className="text-lg sm:text-2xl font-bold text-gray-900">{value}</p>
        <p className="text-xs sm:text-sm text-gray-600 truncate">{label}</p>
      </div>
    </div>
  </div>
);

const CourseLibraryTab = () => {
  const {
    data: masterCourses = [],
    isLoading: loading,
    isError,
    refetch,
  } = useCourseLibrary();
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedType, setSelectedType] = useState('all');
  const [selectedRegulatoryBody, setSelectedRegulatoryBody] = useState('all');
  const [expandedCourses, setExpandedCourses] = useState({});
  const [expandedProviderTitles, setExpandedProviderTitles] = useState({});
  const [showFilters, setShowFilters] = useState(false);
  const [courseFormState, setCourseFormState] = useState(null); // null closed · {} add · course edit

  const toggleCourseExpand = (code) => {
    setExpandedCourses(prev => ({
      ...prev,
      [code]: !prev[code]
    }));
  };

  const toggleProviderTitles = (groupKey) => {
    setExpandedProviderTitles(prev => ({
      ...prev,
      [groupKey]: !prev[groupKey]
    }));
  };

  const expandAll = () => {
    const allExpanded = {};
    masterCourses.forEach(course => {
      allExpanded[course.code] = true;
    });
    setExpandedCourses(allExpanded);
  };

  const collapseAll = () => {
    setExpandedCourses({});
    setExpandedProviderTitles({});
  };

  const handleExport = () => {
    toast.success('Course library exported to Excel');
  };

  const clearFilters = () => {
    setSearchTerm('');
    setSelectedType('all');
    setSelectedRegulatoryBody('all');
  };

  const hasActiveFilters = searchTerm || selectedType !== 'all' || selectedRegulatoryBody !== 'all';
  const expandedCount = Object.values(expandedCourses).filter(Boolean).length;

  const catalogueSummary = useMemo(() => {
    const providerKeys = new Set();
    let offeringCount = 0;
    masterCourses.forEach((course) => {
      getActiveProviderMappings(course).forEach((mapping) => {
        offeringCount += 1;
        providerKeys.add(
          mapping.training_provider_uen
          || mapping.training_provider
          || mapping.training_provider_name,
        );
      });
    });
    providerKeys.delete(undefined);
    providerKeys.delete(null);
    providerKeys.delete('');
    return {
      offeringCount,
      providerCount: providerKeys.size,
    };
  }, [masterCourses]);

  const filteredCourses = masterCourses.filter(course => {
    const normalizedSearch = searchTerm.trim().toLowerCase();
    const searchableValues = [
      course.code,
      course.name,
      course.grants_certification_name,
      ...getActiveProviderMappings(course).flatMap((mapping) => [
        mapping.provider_course_name,
        mapping.training_provider_name,
        mapping.training_provider_trading_name,
        mapping.provider_display_name,
      ]),
    ];
    const matchesSearch = !normalizedSearch || searchableValues.some(
      (value) => String(value || '').toLowerCase().includes(normalizedSearch),
    );
    const matchesType = selectedType === 'all' || course.course_type === selectedType;
    const matchesRegulatoryBody =
      selectedRegulatoryBody === 'all' || course.regulatory_body === selectedRegulatoryBody;
    return matchesSearch && matchesType && matchesRegulatoryBody;
  });

  const providerGroupsByCourse = useMemo(() => {
    const map = {};
    filteredCourses.forEach((course) => {
      map[course.code] = groupMappingsByProvider(getActiveProviderMappings(course));
    });
    return map;
  }, [filteredCourses]);

  const getCourseTypeLabel = (type) => {
    const labels = {
      'initial': 'Initial Certification',
      'renewal': 'Renewal/Recertification',
      'refresher': 'Refresher',
      'awareness': 'Awareness'
    };
    return labels[type] || type;
  };

  const getCourseTypeStatus = (type) => {
    const statusMap = {
      'initial': 'approved',
      'renewal': 'pending',
      'refresher': 'in_progress',
      'awareness': 'draft'
    };
    return statusMap[type] || 'draft';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (isError) {
    return (
      <div className="card">
        <div className="card-content text-center py-12">
          <BookOpen className="w-10 h-10 text-gray-300 mx-auto mb-3" />
          <h3 className="text-base font-semibold text-gray-900">Course library unavailable</h3>
          <p className="text-sm text-gray-500 mt-1 mb-4">
            The catalogue could not be loaded.
          </p>
          <button type="button" onClick={() => refetch()} className="btn btn-admin-outline">
            Try again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3">
        <div className="min-w-0">
          <h2 className="text-lg sm:text-2xl font-bold text-gray-900 flex items-center gap-2">
            <span className="truncate">Course Library</span>
            <CountBadge count={masterCourses.length} theme="admin" size="md" />
          </h2>
          <p className="text-xs sm:text-sm text-gray-500 mt-1 truncate">
            Canonical courses and approved provider offerings
          </p>
        </div>
        <button
          onClick={() => setCourseFormState({})}
          className="btn btn-admin-primary w-11 h-11 min-h-0 max-h-11 p-0 gap-0 shrink-0 sm:w-auto sm:max-h-none sm:min-h-[44px] sm:h-auto sm:px-4 sm:py-2.5 sm:gap-2 flex-shrink-0 self-start sm:self-auto"
          aria-label="Add Master Course"
        >
          <Plus className="w-4 h-4" />
          <span className="hidden sm:inline">Add Master Course</span>
        </button>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-6">
        <MetricCard
          icon={BookOpen}
          iconClasses="bg-red-100 text-red-600"
          value={masterCourses.length}
          label="Master courses"
        />
        <MetricCard
          icon={Layers3}
          iconClasses="bg-purple-100 text-purple-600"
          value={catalogueSummary.offeringCount}
          label="Provider offerings"
        />
        <MetricCard
          icon={Building}
          iconClasses="bg-blue-100 text-blue-600"
          value={catalogueSummary.providerCount}
          label="Training centres"
        />
      </div>

      {/* Search and Toolbar */}
      <div className="card">
        <div className="card-content">
          <div className="flex items-center justify-between gap-4">
            <DebouncedSearchInput
              value={searchTerm}
              onSearch={setSearchTerm}
              placeholder="Search courses, provider titles, or training centres..."
              className="max-w-xl"
            />

            <div className="flex items-center gap-2">
              <FilterButton
                theme="admin"
                isOpen={showFilters}
                activeCount={
                  (searchTerm ? 1 : 0) +
                  (selectedType !== 'all' ? 1 : 0) +
                  (selectedRegulatoryBody !== 'all' ? 1 : 0)
                }
                onClick={() => setShowFilters(!showFilters)}
              />
              <button
                onClick={handleExport}
                className="icon-button-admin"
                disabled={filteredCourses.length === 0}
                title="Export to Excel"
                aria-label="Export to Excel"
              >
                <Download className="w-4 h-4" />
              </button>
              <button
                onClick={expandedCount > 0 ? collapseAll : expandAll}
                className="icon-button-admin"
                title={expandedCount > 0 ? 'Collapse All' : 'Expand All'}
                aria-label={expandedCount > 0 ? 'Collapse All' : 'Expand All'}
              >
                <ChevronsUpDown className="w-4 h-4" />
              </button>
            </div>
          </div>

          {showFilters && (
            <div className="mt-4 pt-4 border-t border-gray-200">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Course Type</label>
                  <select
                    value={selectedType}
                    onChange={(e) => setSelectedType(e.target.value)}
                    className="select-admin w-full"
                  >
                    <option value="all">All Course Types</option>
                    <option value="initial">Initial Certification</option>
                    <option value="renewal">Renewal/Recertification</option>
                    <option value="refresher">Refresher</option>
                    <option value="awareness">Awareness</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Regulatory Body</label>
                  <select
                    value={selectedRegulatoryBody}
                    onChange={(e) => setSelectedRegulatoryBody(e.target.value)}
                    className="select-admin w-full"
                  >
                    {REGULATORY_BODY_FILTER_OPTIONS.map((opt) => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
          )}

          {hasActiveFilters && (
            <div className="mt-3 flex items-center gap-2 flex-wrap">
              {searchTerm && (
                <span className="pill pill-secondary flex items-center gap-1">
                  Search: "{searchTerm}"
                  <button onClick={() => setSearchTerm('')} className="ml-1 hover:text-red-600">
                    <X className="w-3 h-3" />
                  </button>
                </span>
              )}
              {selectedType !== 'all' && (
                <span className="pill pill-secondary flex items-center gap-1">
                  Type: {getCourseTypeLabel(selectedType)}
                  <button onClick={() => setSelectedType('all')} className="ml-1 hover:text-red-600">
                    <X className="w-3 h-3" />
                  </button>
                </span>
              )}
              {selectedRegulatoryBody !== 'all' && (
                <span className="pill pill-secondary flex items-center gap-1">
                  Body: {getRegulatoryBodyLabel(selectedRegulatoryBody)}
                  <button onClick={() => setSelectedRegulatoryBody('all')} className="ml-1 hover:text-red-600">
                    <X className="w-3 h-3" />
                  </button>
                </span>
              )}
              <button onClick={clearFilters} className="text-xs text-red-600 hover:underline ml-2">
                Clear all
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Results Count + Legend */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 text-sm text-gray-500">
        <span className="truncate">
          Showing {filteredCourses.length} of {masterCourses.length} master courses
          {' · '}
          {catalogueSummary.offeringCount} provider offerings
        </span>
        <div className="flex items-center gap-2 sm:gap-3 text-xs flex-wrap">
          <StatusBadge status="approved" label="Initial" />
          <StatusBadge status="pending" label="Renewal" />
          <StatusBadge status="in_progress" label="Refresher" />
          <StatusBadge status="draft" label="Awareness" />
        </div>
      </div>

      {/* Course List */}
      <div className="space-y-2">
        {filteredCourses.map((course) => {
          const providerGroups = providerGroupsByCourse[course.code] || [];
          const uniqueProviderCount = providerGroups.length;
          const catalogueTitleCount = getActiveProviderMappings(course).length;
          const certLabel = getCertificationLabel(course);
          const showCertLine = shouldShowCertificationLine(course, certLabel);

          return (
            <AccordionSection
              key={course.code}
              expanded={!!expandedCourses[course.code]}
              onToggle={() => toggleCourseExpand(course.code)}
              theme="admin"
              contentVariant="flush"
              title={
                <div className="min-w-0">
                  <div className="flex items-baseline gap-2 min-w-0">
                    <span className="font-medium text-gray-900 truncate">{course.name}</span>
                    <span className="text-xs text-gray-500 font-mono flex-shrink-0">{course.code}</span>
                  </div>
                  {showCertLine && (
                    <div className="text-xs text-gray-500 truncate mt-0.5">
                      Certification: {certLabel}
                    </div>
                  )}
                </div>
              }
              trailing={
                <div className="flex items-center gap-3">
                  <StatusBadge
                    status={getCourseTypeStatus(course.course_type)}
                    label={getCourseTypeLabel(course.course_type)}
                  />
                  <div className="text-center min-w-[80px]">
                    <span className="text-lg font-semibold text-gray-700">
                      {uniqueProviderCount}
                    </span>
                    <p className="text-xs text-gray-500">providers</p>
                  </div>
                  {/* span (not button) — nested inside the accordion header <button> */}
                  <span
                    role="button"
                    tabIndex={0}
                    onClick={(e) => { e.stopPropagation(); setCourseFormState(course); }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.stopPropagation();
                        e.preventDefault();
                        setCourseFormState(course);
                      }
                    }}
                    className="icon-button-admin"
                    aria-label={`Edit ${course.name}`}
                    title="Edit course"
                  >
                    <Pencil className="w-4 h-4" />
                  </span>
                </div>
              }
            >
              {providerGroups.length > 0 ? (
                <div className="border-t bg-gray-50 p-4">
                  <div className="flex items-baseline justify-between gap-3 mb-3">
                    <h4 className="text-sm font-medium text-gray-700">
                      Training Centres · {providerGroups.length}
                    </h4>
                    {catalogueTitleCount > providerGroups.length && (
                      <span className="text-xs text-gray-500">
                        {catalogueTitleCount} catalogue titles
                      </span>
                    )}
                  </div>

                  <div className="space-y-2">
                    {providerGroups.map((group) => {
                      const titlesKey = `${course.code}:${group.key}`;
                      const titlesExpanded = !!expandedProviderTitles[titlesKey];
                      const showLegalSecondary =
                        group.legalName &&
                        group.tradingName &&
                        group.legalName.toLowerCase() !== group.tradingName.toLowerCase() &&
                        group.displayName.toLowerCase() !== group.legalName.toLowerCase();

                      return (
                        <div
                          key={group.key}
                          className="p-3 bg-white rounded-lg border border-gray-100 hover:border-gray-300 transition-colors duration-200"
                        >
                          <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
                            <div className="flex items-start gap-3 min-w-0">
                              <div className="w-8 h-8 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0">
                                <Building className="w-4 h-4 text-red-600" />
                              </div>
                              <div className="min-w-0">
                                <p className="font-medium text-gray-900 text-sm truncate">
                                  {group.displayName}
                                </p>
                                {showLegalSecondary && (
                                  <p className="text-xs text-gray-500 truncate">
                                    {group.legalName}
                                  </p>
                                )}
                              </div>
                            </div>

                            {group.languages.length > 0 && (
                              <div className="flex items-center gap-1.5 flex-wrap pl-11 sm:pl-0 sm:justify-end">
                                <Globe className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" aria-hidden="true" />
                                {group.languages.map((lang) => (
                                  <span
                                    key={lang}
                                    className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-700"
                                  >
                                    {lang}
                                  </span>
                                ))}
                              </div>
                            )}
                          </div>

                          {group.titleCount > 0 && (
                            <div className="mt-2 pl-11">
                              {group.titleCount === 1 ? (
                                <p className="text-sm text-gray-600 truncate">
                                  {group.titles[0].name}
                                </p>
                              ) : (
                                <>
                                  <button
                                    type="button"
                                    onClick={() => toggleProviderTitles(titlesKey)}
                                    className="inline-flex items-center gap-1 text-xs font-medium text-red-600 hover:text-red-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-500 focus-visible:ring-offset-1 rounded"
                                    aria-expanded={titlesExpanded}
                                  >
                                    <ChevronDown
                                      className={`w-3.5 h-3.5 transition-transform duration-200 ${titlesExpanded ? 'rotate-180' : ''}`}
                                    />
                                    {group.titleCount} catalogue titles
                                  </button>
                                  {titlesExpanded && (
                                    <ul className="mt-2 space-y-1.5 border-l-2 border-gray-100 pl-3">
                                      {group.titles.map((title, idx) => (
                                        <li key={title.id || idx} className="text-sm text-gray-600">
                                          <span className="block truncate">{title.name}</span>
                                          {title.languages.length > 0 && (
                                            <span className="text-xs text-gray-400">
                                              {title.languages.join(' · ')}
                                            </span>
                                          )}
                                        </li>
                                      ))}
                                    </ul>
                                  )}
                                </>
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              ) : (
                <div className="border-t bg-gray-50 p-4">
                  <p className="text-sm text-gray-500 text-center">
                    No provider mappings yet.
                    <button className="text-red-600 hover:underline ml-1">Add provider mapping</button>
                  </p>
                </div>
              )}
            </AccordionSection>
          );
        })}
      </div>

      {/* Empty State */}
      {filteredCourses.length === 0 && (
        <div className="card">
          <div className="card-content">
            <div className="text-center py-12">
              <div className="w-16 h-16 bg-red-50 rounded-full flex items-center justify-center mx-auto mb-4">
                <BookOpen className="w-8 h-8 text-red-300" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                {hasActiveFilters ? 'No Matching Courses' : 'No Courses Found'}
              </h3>
              <p className="text-gray-500 text-sm mb-4">
                {hasActiveFilters
                  ? 'Try adjusting your search or filters.'
                  : 'Get started by adding your first master course to the library.'}
              </p>
              {hasActiveFilters ? (
                <button onClick={clearFilters} className="btn btn-admin-outline">
                  <X className="w-4 h-4 mr-2" />
                  Clear Filters
                </button>
              ) : (
                <button onClick={() => setCourseFormState({})} className="btn btn-admin-primary">
                  <Plus className="w-4 h-4 mr-2" />
                  Add First Course
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {courseFormState !== null && (
        <MasterCourseFormModal
          course={courseFormState.code ? courseFormState : null}
          onClose={() => setCourseFormState(null)}
        />
      )}
    </div>
  );
};

export default CourseLibraryTab;
